import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink } from 'lucide-react';
import { etiquetaSinIdCrudo, type HubspotDealListItem } from '../types/opportunity.types';

interface Props {
  deals: HubspotDealListItem[];
  loading?: boolean;
  onSelect?: (deal: HubspotDealListItem) => void;
  selectionMode?: boolean;
  /** Mapa stageId → label legible. Si no se pasa, mostramos el id crudo. */
  stageLabels?: Map<string, string>;
  /** Si la etapa o el pipeline no tiene nombre conocido y es un ID numérico de HubSpot, no se muestra nada. */
  sinIdsCrudos?: boolean;
  /** Mapa pipelineId → label legible. */
  pipelineLabels?: Map<string, string>;
}

function fmtAmount(a: string | null): string {
  if (a === null) return '—';
  const n = parseFloat(a);
  return Number.isFinite(n)
    ? new Intl.NumberFormat('es-BO', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(n)
    : '—';
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-BO');
}

function isDatePast(d: string | null): boolean {
  if (!d) return false;
  return new Date(d) < new Date();
}

function isDateSoon(d: string | null): boolean {
  if (!d) return false;
  const diff = new Date(d).getTime() - Date.now();
  return diff >= 0 && diff < 7 * 24 * 60 * 60 * 1000;
}

/** Colorea el badge de stage según el label (ganado / perdido / en curso). */
function getStageClassName(label: string | null): string {
  if (!label) return '';
  const lower = label.toLowerCase();
  if (lower.includes('ganado') || lower.includes('won') || lower.includes('closed won')) {
    return 'bg-ok-suave text-ok border-ok-borde';
  }
  if (lower.includes('perdido') || lower.includes('lost') || lower.includes('closed lost')) {
    return 'bg-bloqueo-suave text-bloqueo border-bloqueo-borde';
  }
  return 'bg-info-suave text-info border-info-borde';
}

const PIPELINE_PALETTE = [
  'bg-info-suave text-info border-info-borde',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-alerta-suave text-alerta border-alerta-borde',
  'bg-ok-suave text-ok border-ok-borde',
  'bg-bloqueo-suave text-bloqueo border-bloqueo-borde',
  'bg-info-suave text-info border-info-borde',
];

const pipelineColorCache = new Map<string, string>();

/** Asigna un color estable a cada pipeline por hash de su label. */
function getPipelineClassName(label: string): string {
  if (pipelineColorCache.has(label)) return pipelineColorCache.get(label)!;
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) & 0xffff;
  }
  const cls = PIPELINE_PALETTE[hash % PIPELINE_PALETTE.length];
  pipelineColorCache.set(label, cls);
  return cls;
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-10" />
        </div>
      ))}
    </div>
  );
}

export function OpportunitiesTable({
  deals,
  loading,
  onSelect,
  selectionMode,
  stageLabels,
  pipelineLabels,
  sinIdsCrudos = false,
}: Props) {
  if (loading) {
    return (
      <Card className="border rounded-lg overflow-hidden">
        <TableSkeleton />
      </Card>
    );
  }

  if (!deals.length) {
    return (
      <Card className="border rounded-lg">
        <div className="p-12 text-center space-y-2">
          <p className="text-muted-foreground font-medium">
            No hay oportunidades que coincidan con los filtros
          </p>
          <p className="text-sm text-muted-foreground">
            Ajustá los criterios de búsqueda para ver resultados.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="text-xs text-muted-foreground w-24">ID</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Etapa</TableHead>
              <TableHead>Embudo</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Cierre</TableHead>
              <TableHead className="text-center">Cot.</TableHead>
              {!selectionMode && <TableHead className="w-10"></TableHead>}
              {selectionMode && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {deals.map((d) => {
              const stageLabel = sinIdsCrudos
                ? etiquetaSinIdCrudo(d.stage, stageLabels)
                : d.stage
                  ? (stageLabels?.get(d.stage) ?? d.stage)
                  : null;
              const pipelineLabel = sinIdsCrudos
                ? etiquetaSinIdCrudo(d.pipeline, pipelineLabels)
                : d.pipeline
                  ? (pipelineLabels?.get(d.pipeline) ?? d.pipeline)
                  : null;
              const past = isDatePast(d.closeDate);
              const soon = isDateSoon(d.closeDate);
              const quoteCount = d._count.pricerDatecQuotes;
              // Una cerrada sólo llega al selector si se la buscó por su ID: se ve
              // (para saber que existe) pero no se le asocia una cotización nueva.
              const elegible = !d.cerrada;

              return (
                <TableRow
                  key={d.id}
                  aria-disabled={elegible ? undefined : true}
                  title={
                    elegible
                      ? undefined
                      : 'Oportunidad cerrada: no se le puede asociar una cotización nueva'
                  }
                  className={`transition-colors ${elegible ? 'hover:bg-muted/40' : 'opacity-60'} ${selectionMode && elegible ? 'cursor-pointer' : ''}`}
                  onClick={selectionMode && elegible ? () => onSelect?.(d) : undefined}
                >
                  {/* ID */}
                  <TableCell className="font-mono text-xs text-muted-foreground/70">
                    {d.hubspotDealId}
                  </TableCell>

                  {/* Nombre */}
                  <TableCell className="font-medium max-w-[200px] truncate">{d.name}</TableCell>

                  {/* Empresa */}
                  <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">
                    {d.hubspotCompany?.name ?? '—'}
                  </TableCell>

                  {/* Etapa */}
                  <TableCell>
                    {stageLabel ? (
                      <Badge
                        variant="outline"
                        className={`text-xs ${getStageClassName(stageLabel)}`}
                      >
                        {stageLabel}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {/* Pipeline */}
                  <TableCell>
                    {pipelineLabel ? (
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getPipelineClassName(pipelineLabel)}`}
                      >
                        {pipelineLabel}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {/* Monto */}
                  <TableCell
                    className={`text-right tabular-nums text-sm ${d.amount === null ? 'text-muted-foreground' : 'font-medium'}`}
                  >
                    {fmtAmount(d.amount)}
                  </TableCell>

                  {/* Fecha de cierre */}
                  <TableCell>
                    <span
                      className={`text-sm ${past ? 'text-bloqueo font-semibold' : soon ? 'text-alerta font-semibold' : ''}`}
                    >
                      {fmtDate(d.closeDate)}
                    </span>
                  </TableCell>

                  {/* Cotizaciones */}
                  <TableCell className="text-center">
                    {quoteCount > 0 ? (
                      <Badge variant="info" className="text-xs">
                        {quoteCount}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs opacity-60">
                        0
                      </Badge>
                    )}
                  </TableCell>

                  {/* Acción: Ver en HubSpot */}
                  {!selectionMode && (
                    <TableCell>
                      <a
                        href={`https://app.hubspot.com/contacts/0/deal/${d.hubspotDealId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Ver en HubSpot"
                      >
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </a>
                    </TableCell>
                  )}

                  {/* Modo selección */}
                  {selectionMode && !elegible && (
                    <TableCell>
                      <span className="text-xs font-medium text-muted-foreground">Cerrada</span>
                    </TableCell>
                  )}
                  {selectionMode && elegible && (
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect?.(d);
                        }}
                      >
                        Seleccionar
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
